import { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Search, X, Loader2, ChevronLeft, ChevronRight, SlidersHorizontal, Camera, Sparkles, Mic, MicOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  searchShopifyProductsWithRelated,
  searchShopifyCollections,
  ShopifyProduct,
  ShopifyCollection,
  SearchResult,
} from "@/lib/shopify";
import { useGlobalPreload } from "@/hooks/useGlobalPreload";
import { ImageSearchUploader, VisualMatch } from "@/components/search/ImageSearchUploader";
import { useStaticTranslation } from "@/lib/staticTranslations";
import { searchSitePages } from "./searchPages";
import { useSpeechSearch } from "./useSpeechSearch";
import { SearchProductGrid } from "./SearchProductGrid";
import { SearchFilters } from "./SearchFilters";
import { SearchVisualResults } from "./SearchVisualResults";
import { SearchPagesResults } from "./SearchPagesResults";
import { SearchCategoriesResults } from "./SearchCategoriesResults";

export const SearchDialog = () => {
  const { t: st, isHebrew } = useStaticTranslation("search");
  const dirAttr = isHebrew ? ("rtl" as const) : ("ltr" as const);
  const textAlign = isHebrew ? "text-right" : "text-left";
  const ChevronDir = isHebrew ? ChevronLeft : ChevronRight;

  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResult, setSearchResult] = useState<SearchResult | null>(null);
  const [categoryResults, setCategoryResults] = useState<ShopifyCollection[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const searchRequestIdRef = useRef(0);

  // Image search states
  const [isImageSearchMode, setIsImageSearchMode] = useState(false);
  const [isAnalyzingImage, setIsAnalyzingImage] = useState(false);
  const [imageSearchDescription, setImageSearchDescription] = useState<string | null>(null);
  const [visualMatches, setVisualMatches] = useState<VisualMatch[]>([]);
  const [isVisualSearchResult, setIsVisualSearchResult] = useState(false);

  // Filter states
  const [showFilters, setShowFilters] = useState(false);
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 5000]);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const sitePageResults = useMemo(() => searchSitePages(searchQuery), [searchQuery]);

  const {
    isLoaded,
    isLoading,
    searchSettings,
    defaultProducts,
    allProducts,
    allCollections,
    preload,
  } = useGlobalPreload();

  useEffect(() => {
    preload();
  }, [preload]);

  const maxPrice = useMemo(() => {
    const prices = allProducts.map((p) =>
      parseFloat(p.priceRange?.minVariantPrice?.amount || "0")
    );
    return Math.max(...prices, 5000);
  }, [allProducts]);

  useEffect(() => {
    if (maxPrice > 0) setPriceRange([0, Math.ceil(maxPrice)]);
  }, [maxPrice]);

  const availableCategories = useMemo(
    () =>
      allCollections.map((c) => ({
        handle: c.node.handle,
        title: c.node.title,
        image: c.node.image?.url,
      })),
    [allCollections]
  );

  const doSearch = useCallback(
    async (q: string) => {
      const requestId = ++searchRequestIdRef.current;
      setIsSearching(true);
      setHasSearched(false);

      try {
        const [result, collections] = await Promise.all([
          searchShopifyProductsWithRelated(q, 40),
          searchSettings.show_categories ? searchShopifyCollections(q) : Promise.resolve([]),
        ]);

        if (searchRequestIdRef.current !== requestId) return;

        setSearchResult(result);
        setCategoryResults(collections);
        setHasSearched(true);
      } catch (error) {
        console.error("Error searching:", error);
        if (searchRequestIdRef.current === requestId) {
          setSearchResult(null);
          setCategoryResults([]);
          setHasSearched(true);
        }
      } finally {
        if (searchRequestIdRef.current === requestId) {
          setIsSearching(false);
        }
      }
    },
    [searchSettings.show_categories]
  );

  const handleSearch = useCallback((query: string) => {
    setSearchQuery(query);
    if (query.trim().length >= 2) {
      setIsSearching(true);
      setHasSearched(false);
    }
  }, []);

  // Voice search
  const {
    isListening,
    isSupported: speechSupported,
    startListening,
    stopListening,
  } = useSpeechSearch({
    onResult: (transcript) => {
      setSearchQuery(transcript);
      if (transcript.trim().length >= 2) {
        setIsSearching(true);
        setHasSearched(false);
      }
    },
  });

  // Debounced remote search
  useEffect(() => {
    if (!open) return;

    const q = searchQuery.trim();
    if (q.length < 2) {
      setSearchResult(null);
      setCategoryResults([]);
      setIsSearching(false);
      setHasSearched(false);
      return;
    }

    const timer = window.setTimeout(() => {
      void doSearch(q);
    }, 250);

    return () => window.clearTimeout(timer);
  }, [open, searchQuery, doSearch]);

  const resetSearchState = useCallback(() => {
    setSearchQuery("");
    setSearchResult(null);
    setCategoryResults([]);
    setIsSearching(false);
    setHasSearched(false);
    setShowFilters(false);
    setSelectedCategory(null);
    setPriceRange([0, Math.ceil(maxPrice)]);
    setIsImageSearchMode(false);
    setIsAnalyzingImage(false);
    setImageSearchDescription(null);
    setVisualMatches([]);
    setIsVisualSearchResult(false);
  }, [maxPrice]);

  const handleClear = () => {
    setSearchQuery("");
    setSearchResult(null);
    setCategoryResults([]);
    setHasSearched(false);
    setImageSearchDescription(null);
    setVisualMatches([]);
    setIsVisualSearchResult(false);
  };

  const handleClose = () => {
    setOpen(false);
    resetSearchState();
  };

  // Image search handlers
  const handleImageAnalyzeComplete = useCallback(
    (searchTerms: string[], description: string, matches?: VisualMatch[]) => {
      setIsImageSearchMode(false);
      setIsAnalyzingImage(false);
      setImageSearchDescription(description);

      if (matches && matches.length > 0) {
        setVisualMatches(matches);
        setIsVisualSearchResult(true);
        setHasSearched(true);
        setSearchQuery(searchTerms[0] || "חיפוש ויזואלי");
      } else {
        const query = searchTerms[0] || "תכשיט";
        setSearchQuery(query);
        doSearch(query);
      }
    },
    [doSearch]
  );

  const handleImageSearchCancel = useCallback(() => {
    setIsImageSearchMode(false);
    setIsAnalyzingImage(false);
  }, []);

  const clearFilters = () => {
    setSelectedCategory(null);
    setPriceRange([0, Math.ceil(maxPrice)]);
  };

  const hasActiveFilters =
    selectedCategory !== null || priceRange[0] > 0 || priceRange[1] < Math.ceil(maxPrice);

  const exactMatches = searchResult?.exactMatches || [];
  const relatedProducts = searchResult?.relatedProducts || [];
  const fallbackTerm = searchResult?.fallbackTerm;

  const displayProducts = useMemo(() => {
    let products: ShopifyProduct[];

    if (searchQuery.trim().length >= 2) {
      products = [
        ...exactMatches,
        ...relatedProducts.filter((p) => !exactMatches.some((e) => e.id === p.id)),
      ];
    } else {
      products = defaultProducts.length > 0 ? defaultProducts : allProducts.slice(0, 12);
    }

    return products.filter((product) => {
      const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
      return price >= priceRange[0] && price <= priceRange[1];
    });
  }, [searchQuery, exactMatches, relatedProducts, defaultProducts, allProducts, priceRange]);

  const displayExact = useMemo(
    () =>
      exactMatches.filter((product) => {
        const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
        return price >= priceRange[0] && price <= priceRange[1];
      }),
    [exactMatches, priceRange]
  );

  const displayRelated = useMemo(
    () =>
      relatedProducts
        .filter((p) => !exactMatches.some((e) => e.id === p.id))
        .filter((product) => {
          const price = parseFloat(product.priceRange?.minVariantPrice?.amount || "0");
          return price >= priceRange[0] && price <= priceRange[1];
        }),
    [relatedProducts, exactMatches, priceRange]
  );

  const hasResults = searchQuery.trim().length >= 2;
  const showCategories = hasResults && categoryResults.length > 0 && searchSettings.show_categories;
  const showRelatedSection =
    hasResults && hasSearched && !isSearching && displayExact.length < 4 && displayRelated.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
        if (!isOpen) resetSearchState();
      }}
    >
      <DialogTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="hover:bg-transparent"
          aria-label={st("triggerLabel", "חיפוש מוצרים")}
        >
          <Search className="h-5 w-5 text-primary" strokeWidth={1.5} />
        </Button>
      </DialogTrigger>
      <DialogContent
        className="sm:max-w-4xl max-h-[90vh] sm:max-h-[85vh] flex flex-col p-4 sm:p-6"
        dir={dirAttr}
      >
        <DialogHeader className="pb-2">
          <DialogTitle className={`${textAlign} text-lg sm:text-xl font-light`}>
            {st("title", "חיפוש מוצרים")}
          </DialogTitle>
        </DialogHeader>

        {isImageSearchMode ? (
          <div className="py-4">
            <ImageSearchUploader
              onAnalyzeComplete={handleImageAnalyzeComplete}
              onCancel={handleImageSearchCancel}
              isAnalyzing={isAnalyzingImage}
              setIsAnalyzing={setIsAnalyzingImage}
            />
          </div>
        ) : (
          <>
            {/* Search Input */}
            <div className="relative">
              <Search
                className={`absolute ${isHebrew ? "right-3" : "left-3"} top-3 h-5 w-5 text-muted-foreground`}
              />
              <Input
                type="text"
                placeholder={st("placeholder", "חפש תכשיטים לפי שם...")}
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className={`${
                  isHebrew ? "pr-10 pl-36 text-right" : "pl-10 pr-36 text-left"
                } h-12 text-base`}
                autoFocus
              />
              <div
                className={`absolute ${isHebrew ? "left-2" : "right-2"} top-2 flex items-center gap-0`}
              >
                {speechSupported && (
                  <Button
                    variant="ghost"
                    size="icon"
                    className={`h-8 w-8 relative transition-colors ${
                      isListening
                        ? "text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                        : "text-primary hover:text-primary/80 hover:bg-primary/10"
                    }`}
                    onClick={isListening ? stopListening : startListening}
                    title={
                      isListening ? st("voiceStop", "הפסק הקלטה") : st("voiceStart", "חיפוש קולי")
                    }
                  >
                    {isListening ? (
                      <MicOff className="h-4 w-4 z-10 relative" />
                    ) : (
                      <Mic className="h-4 w-4" />
                    )}
                    {isListening && (
                      <>
                        <span className="absolute inset-0 rounded-full border-2 border-destructive/60 animate-ping" />
                        <span className="absolute inset-1 rounded-full bg-destructive/10 animate-pulse" />
                      </>
                    )}
                  </Button>
                )}
                <div className="w-px h-4 bg-border" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary hover:text-primary/80 hover:bg-primary/10"
                  onClick={() => setIsImageSearchMode(true)}
                  title={st("imageSearch", "חיפוש לפי תמונה")}
                >
                  <Camera className="h-4 w-4" />
                </Button>
                <div className="w-px h-4 bg-border" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-primary hover:text-primary/80 hover:bg-primary/10"
                  onClick={() => setShowFilters(!showFilters)}
                  title={st("smartFilter", "סינון חכם")}
                >
                  <SlidersHorizontal className="h-4 w-4" />
                </Button>
                {searchQuery && (
                  <Button variant="ghost" size="icon" className="h-8 w-8" onClick={handleClear}>
                    <X className="h-4 w-4" />
                  </Button>
                )}
              </div>
            </div>

            {/* Image search description badge */}
            {imageSearchDescription && (
              <div className="flex items-center gap-2 text-sm bg-primary/10 text-primary px-3 py-2 rounded-lg">
                {isVisualSearchResult ? (
                  <Sparkles className="h-4 w-4" />
                ) : (
                  <Camera className="h-4 w-4" />
                )}
                <span>
                  {isVisualSearchResult
                    ? `${st("visualSearchAdvanced", "חיפוש ויזואלי מתקדם:")} ${imageSearchDescription}`
                    : `${st("imageSearchLabel", "חיפוש לפי תמונה:")} ${imageSearchDescription}`}
                </span>
                {visualMatches.length > 0 && (
                  <span className="text-xs bg-primary/20 px-2 py-0.5 rounded-full">
                    {visualMatches.length} {st("results", "תוצאות")}
                  </span>
                )}
                <Button
                  variant="ghost"
                  size="icon"
                  className={`h-5 w-5 ${isHebrew ? "mr-auto" : "ml-auto"}`}
                  onClick={handleClear}
                >
                  <X className="h-3 w-3" />
                </Button>
              </div>
            )}

            <SearchFilters
              open={showFilters}
              onOpenChange={setShowFilters}
              priceRange={priceRange}
              setPriceRange={setPriceRange}
              maxPrice={maxPrice}
              selectedCategory={selectedCategory}
              setSelectedCategory={setSelectedCategory}
              availableCategories={availableCategories}
              hasActiveFilters={hasActiveFilters}
              onClearFilters={clearFilters}
              t={st}
            />

            {/* Results count */}
            {hasResults && hasSearched && !isSearching && (
              <div className={`text-sm text-muted-foreground ${textAlign} flex items-center gap-2 py-1`}>
                <span>{st("foundProducts", `נמצאו ${displayProducts.length} מוצרים`)} </span>
                {sitePageResults.length > 0 && (
                  <span>
                    • {sitePageResults.length} {st("pages", "עמודים")}
                  </span>
                )}
                {showCategories && (
                  <span>
                    • {categoryResults.length} {st("categoriesCount", "קטגוריות")}
                  </span>
                )}
                {hasActiveFilters && (
                  <span className="text-primary text-xs">{st("filtered", "(מסונן)")}</span>
                )}
              </div>
            )}

            <div className="mt-2 flex-1 min-h-0 overflow-y-auto">
              {!isLoaded || isLoading ? (
                <div className="flex items-center justify-center py-16">
                  <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
              ) : isVisualSearchResult && visualMatches.length > 0 ? (
                <SearchVisualResults
                  matches={visualMatches}
                  textAlign={textAlign}
                  onItemClick={handleClose}
                  t={st}
                />
              ) : (
                <>
                  {sitePageResults.length > 0 && !isSearching && (
                    <SearchPagesResults
                      pages={sitePageResults}
                      textAlign={textAlign}
                      ChevronDir={ChevronDir}
                      onItemClick={handleClose}
                      t={st}
                    />
                  )}

                  {showCategories && !isSearching && (
                    <SearchCategoriesResults
                      collections={categoryResults}
                      textAlign={textAlign}
                      ChevronDir={ChevronDir}
                      onItemClick={handleClose}
                      t={st}
                    />
                  )}

                  {hasResults && isSearching ? (
                    <div className="flex flex-col items-center justify-center py-16 gap-4">
                      <div className="gold-bar-loader" />
                      <p className="text-muted-foreground text-sm">{st("searching", "מחפש...")}</p>
                    </div>
                  ) : displayExact.length > 0 ? (
                    <div className="space-y-6">
                      <div>
                        {hasResults && hasSearched && (
                          <h3
                            className={`text-sm font-medium text-muted-foreground mb-2 sm:mb-3 ${textAlign}`}
                          >
                            {displayExact.length === 1
                              ? st("foundProducts", "נמצא מוצר אחד")
                              : st("foundProducts", `נמצאו ${displayExact.length} מוצרים`)}{" "}
                            {st("foundFor", "עבור")} "{searchQuery}"
                          </h3>
                        )}
                        <SearchProductGrid
                          products={displayExact}
                          textAlign={textAlign}
                          onItemClick={handleClose}
                        />
                      </div>

                      {showRelatedSection && (
                        <div className="border-t pt-4">
                          <h3
                            className={`text-sm font-medium text-muted-foreground mb-2 sm:mb-3 ${textAlign}`}
                          >
                            {st("relatedProducts", "מוצרים קשורים שעשויים לעניין אותך")}
                          </h3>
                          <SearchProductGrid
                            products={displayRelated}
                            textAlign={textAlign}
                            onItemClick={handleClose}
                            limit={8}
                          />
                        </div>
                      )}
                    </div>
                  ) : hasResults && hasSearched && !isSearching && displayRelated.length > 0 ? (
                    <div className="space-y-4">
                      <div className="text-center py-4 sm:py-6 bg-muted/30 rounded-lg">
                        <p className="text-muted-foreground text-sm sm:text-base">
                          {st("noExactResults", "לא נמצאו תוצאות מדויקות עבור")} "{searchQuery}"
                        </p>
                        <p className="text-xs sm:text-sm text-muted-foreground/70 mt-1">
                          {st("butWeHaveRelated", "אבל יש לנו מוצרים קשורים")}
                          {fallbackTerm ? ` ${st("forTerm", "ל")}"${fallbackTerm}"` : ""}:
                        </p>
                      </div>
                      <SearchProductGrid
                        products={displayRelated}
                        textAlign={textAlign}
                        onItemClick={handleClose}
                      />
                    </div>
                  ) : hasResults && hasSearched && !isSearching ? (
                    <div className="text-center py-12 sm:py-16">
                      <Search className="h-10 w-10 sm:h-12 sm:w-12 mx-auto text-muted-foreground/50 mb-4" />
                      <p className="text-muted-foreground text-sm sm:text-base">
                        {st("noResults", "לא נמצאו תוצאות עבור")} "{searchQuery}"
                      </p>
                      <p className="text-xs sm:text-sm text-muted-foreground/70 mt-2">
                        {st("tryDifferent", "נסה לחפש מילה אחרת")}
                      </p>
                      {hasActiveFilters && (
                        <Button variant="link" onClick={clearFilters} className="mt-3 text-primary">
                          {st("tryClearFilter", "נסה לנקות את הסינון")}
                        </Button>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {displayProducts.length > 0 ? (
                        <SearchProductGrid
                          products={displayProducts}
                          textAlign={textAlign}
                          onItemClick={handleClose}
                          limit={12}
                        />
                      ) : (
                        <div className="text-center py-6 sm:py-8 text-muted-foreground text-sm">
                          {st("couldNotLoad", "לא הצלחנו לטעון מוצרים כרגע")}
                        </div>
                      )}

                      <div className="text-center text-muted-foreground/80 text-xs sm:text-sm">
                        {st("startTyping", "התחל להקליד כדי לחפש מוצרים")}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
